<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatBytes } from '@/utils/formatBytes'

defineProps<{
  disabled: boolean
  disabledReason?: string
  compact?: boolean
  keepOriginal: boolean
  imageMaxBytes: number
}>()

const emit = defineEmits<{
  files: [files: File[]]
  'add-link': []
  'update:keepOriginal': [value: boolean]
}>()

const { t } = useI18n()

const ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,.heic,.heif'

const inputRef = ref<HTMLInputElement | null>(null)
const dragging = ref(false)

function openPicker(): void {
  inputRef.value?.click()
}

function onChange(event: Event): void {
  const input = event.target as HTMLInputElement
  const picked = Array.from(input.files ?? [])
  input.value = ''
  if (picked.length > 0) emit('files', picked)
}

function onDrop(event: DragEvent): void {
  dragging.value = false
  const dropped = Array.from(event.dataTransfer?.files ?? [])
  if (dropped.length > 0) emit('files', dropped)
}
</script>

<template>
  <div data-testid="gallery-drop-zone">
    <label class="inline-flex items-start gap-2 mb-3 text-sm text-slate-300 cursor-pointer">
      <input
        type="checkbox"
        class="mt-0.5 accent-emerald-500"
        :checked="keepOriginal"
        @change="emit('update:keepOriginal', ($event.target as HTMLInputElement).checked)"
      />
      <span>
        {{ t('galleries.dropzone.keep_original') }}
        <span class="block text-xs text-slate-500">{{ t('galleries.dropzone.keep_original_hint') }}</span>
      </span>
    </label>

    <div
      class="rounded-2xl border-2 border-dashed text-center transition-colors"
      :class="[
        compact ? 'px-4 py-4' : 'px-6 py-10',
        disabled
          ? 'border-slate-700 bg-slate-800/40'
          : dragging
            ? 'border-emerald-500 bg-emerald-500/10'
            : 'border-slate-600 bg-slate-800/40 hover:border-slate-500',
      ]"
      @dragover.prevent="dragging = !disabled"
      @dragleave.prevent="dragging = false"
      @drop.prevent="disabled ? undefined : onDrop($event)"
    >
      <template v-if="disabled">
        <p class="text-sm font-medium text-slate-200">{{ t('galleries.dropzone.disabled_title') }}</p>
        <p class="mt-1 text-sm text-slate-400">{{ disabledReason }}</p>
      </template>
      <template v-else>
        <p class="text-sm font-medium text-slate-200">
          {{ t('galleries.dropzone.title') }}
          <button
            type="button"
            class="text-emerald-400 hover:text-emerald-300 underline underline-offset-2"
            @click="openPicker"
          >
            {{ t('galleries.dropzone.browse') }}
          </button>
        </p>
        <p class="mt-1 text-xs text-slate-500">
          {{ t('galleries.dropzone.hint', { size: formatBytes(imageMaxBytes) }) }}
        </p>
      </template>

      <button
        type="button"
        class="mt-3 text-sm font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
        @click="emit('add-link')"
      >
        {{ t('galleries.dropzone.add_link') }}
      </button>

      <input
        ref="inputRef"
        type="file"
        multiple
        class="sr-only"
        tabindex="-1"
        :accept="ACCEPT"
        :aria-label="t('galleries.dropzone.browse')"
        @change="onChange"
      />
    </div>
  </div>
</template>
